import type { DropdownOption } from "../../redistribution/models/DropdownOption.ts";
import type { IntermediateOrderSkuRow } from "../../redistribution/models/IntermediateOrderSkuRow.ts";
import { getWarehouseDisplay } from "../../utils/warehouseDisplay.ts";
import { DEFAULT_INTERMEDIATE_ORDER_DIVISIONS } from "./intermediateOrderRedistribution.ts";

// The search service returns flat SKU-level rows (one per BO/IO/SKU). The grid
// shows one row per Intermediate Order with its SKUs nested behind a
// "View Items" toggle, mirroring the PO -> SKU nesting on PO Recommendations.
// Reshaping happens here rather than in the service so the Track B API swap
// keeps returning IntermediateOrderSkuRow[] unchanged.

export interface IntermediateOrderRow {
  _isDetail?: false;
  rowKey: string;
  bulkOrderNumber: string;
  intermediatePoNumber: string;
  vendorCode: string;
  vendorName: string;
  warehouseNumber: number;
  warehouseName: string;
  warehouseDisplay: string;
  distributionStatus: string;
  vendorCutDate: string | null;
  shipDateRangeStart: string;
  shipDateRangeEnd: string;
  lastDistributionDate: string;
  skuCount: number;
  totalUnits: number;
  totalCubicFeet: number;
  skus: IntermediateOrderSkuRow[];
}

export interface IntermediateOrderDetailRow {
  _isDetail: true;
  parentRowKey: string;
  intermediatePoNumber: string;
  skus: IntermediateOrderSkuRow[];
}

export type IntermediateOrderGridRow =
  | IntermediateOrderRow
  | IntermediateOrderDetailRow;

export function isDetailRow(
  row: IntermediateOrderGridRow | undefined | null
): row is IntermediateOrderDetailRow {
  return row?._isDetail ?? false;
}

// Groups the flat rows into one parent per Intermediate Order, sorted by BO#
// then IO# so the BO grouping stays contiguous.
//
// Order-level fields (vendor, warehouse, status, dates) are taken from the
// group's first row: they describe the Intermediate Order, so every SKU row
// under one IO carries the same values. Quantities are summed.
export function buildIntermediateOrderRows(
  rows: IntermediateOrderSkuRow[] | undefined,
  warehouseOptions: DropdownOption[] = []
): IntermediateOrderRow[] {
  const byIntermediateOrder = new Map<string, IntermediateOrderSkuRow[]>();

  for (const row of rows ?? []) {
    const key = `${row.bulkOrderNumber}:${row.intermediatePoNumber}`;
    const existing = byIntermediateOrder.get(key);
    if (existing) {
      existing.push(row);
      continue;
    }
    byIntermediateOrder.set(key, [row]);
  }

  const orderRows: IntermediateOrderRow[] = [];

  for (const [rowKey, skus] of byIntermediateOrder) {
    const first = skus[0];
    if (!first) {
      continue;
    }

    const division =
      DEFAULT_INTERMEDIATE_ORDER_DIVISIONS[first.warehouseNumber] ?? "SE";

    orderRows.push({
      rowKey,
      bulkOrderNumber: first.bulkOrderNumber,
      intermediatePoNumber: first.intermediatePoNumber,
      vendorCode: first.vendorCode,
      vendorName: first.vendorName,
      warehouseNumber: first.warehouseNumber,
      warehouseName: first.warehouseName,
      warehouseDisplay: getWarehouseDisplay(
        division,
        first.warehouseNumber,
        warehouseOptions,
        first.warehouseName
      ),
      distributionStatus: first.distributionStatus,
      vendorCutDate: first.vendorCutDate,
      shipDateRangeStart: first.shipDateRangeStart,
      shipDateRangeEnd: first.shipDateRangeEnd,
      lastDistributionDate: first.lastDistributionDate,
      skuCount: skus.length,
      totalUnits: skus.reduce((total, sku) => total + sku.totalUnits, 0),
      totalCubicFeet: skus.reduce(
        (total, sku) => total + sku.totalCubicFeet,
        0
      ),
      skus: skus.toSorted((a, b) => a.skuNumber.localeCompare(b.skuNumber)),
    });
  }

  return orderRows.toSorted(
    (a, b) =>
      a.bulkOrderNumber.localeCompare(b.bulkOrderNumber) ||
      a.intermediatePoNumber.localeCompare(b.intermediatePoNumber)
  );
}
